/*
  Glow & Beam theme script. No framework: each feature binds to data
  attributes in the Liquid markup and degrades to plain forms/links without JS.
*/
(() => {
  const theme = window.theme || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── Money ─────────────────────────────────────────────────────────── */
  function formatMoney(cents) {
    const format = theme.moneyFormat || '₹{{amount_no_decimals}}';
    const value = Number(cents) / 100;
    const withDelims = (n, decimals) =>
      n.toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
        case 'amount_no_decimals_with_comma_separator':
          return withDelims(Math.round(value), 0);
        case 'amount_with_comma_separator':
          return withDelims(value, 2).replace('.', ',');
        default:
          return withDelims(value, 2);
      }
    });
  }

  /* ── Site chrome: publish height, shrink on scroll ─────────────────── */
  function initChrome() {
    const chrome = $('[data-site-chrome]');
    if (!chrome) return;
    const publish = () =>
      document.documentElement.style.setProperty('--chrome-h', `${chrome.offsetHeight}px`);
    publish();
    new ResizeObserver(publish).observe(chrome);

    const onScroll = () => chrome.classList.toggle('is-scrolled', window.scrollY > 16);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ── Drawers ───────────────────────────────────────────────────────── */
  let lastFocus = null;

  function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $$('[aria-controls="' + id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = $('[data-drawer-close]', drawer);
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
    document.dispatchEvent(new CustomEvent('drawer:open', { detail: { id } }));
  }

  function closeDrawer(drawer) {
    if (!drawer || !drawer.classList.contains('is-open')) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    $$('[aria-controls="' + drawer.id + '"]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function initDrawers() {
    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        e.preventDefault();
        openDrawer(opener.dataset.drawerOpen);
        return;
      }
      const closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        e.preventDefault();
        closeDrawer(closer.closest('.drawer'));
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      $$('.drawer.is-open').forEach(closeDrawer);
    });
  }

  /* ── Cart ──────────────────────────────────────────────────────────── */
  const cartDrawerId = 'CartDrawer';
  const onCartPage = () => !!$('[data-cart-page]');

  async function renderCartSections() {
    const ids = ['cart-drawer'];
    const page = $('[data-cart-page]');
    if (page) ids.push(page.dataset.sectionId);
    const res = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
    const sections = await res.json();

    const drawerHtml = sections['cart-drawer'];
    const drawer = document.getElementById(cartDrawerId);
    if (drawerHtml && drawer) {
      const fresh = new DOMParser().parseFromString(drawerHtml, 'text/html').getElementById(cartDrawerId);
      if (fresh) {
        const panel = $('.drawer__panel', drawer);
        const freshPanel = $('.drawer__panel', fresh);
        if (panel && freshPanel) panel.innerHTML = freshPanel.innerHTML;
      }
    }
    if (page && sections[page.dataset.sectionId]) {
      const fresh = new DOMParser()
        .parseFromString(sections[page.dataset.sectionId], 'text/html')
        .querySelector('[data-cart-page]');
      if (fresh) page.innerHTML = fresh.innerHTML;
    }

    const cart = await fetch(`${theme.routes.cart}.js`).then((r) => r.json());
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    $$('[data-cart-toggle]').forEach((el) => {
      el.setAttribute('aria-label', cart.item_count ? `Open cart, ${cart.item_count} item${cart.item_count === 1 ? '' : 's'}` : 'Open cart');
    });
    return cart;
  }

  function setCartLoading(on) {
    const drawer = document.getElementById(cartDrawerId);
    if (drawer) drawer.classList.toggle('is-loading', on);
    $$('[data-cart-page]').forEach((el) => el.classList.toggle('is-loading', on));
  }

  function showCartError(message) {
    $$('[data-cart-error]').forEach((el) => {
      el.textContent = message || theme.strings.cartError;
      el.hidden = false;
    });
  }

  async function addToCart(items) {
    const res = await fetch(`${theme.routes.cartAdd}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.description || data.message || theme.strings.cartError);
    await renderCartSections();
    if (!onCartPage()) openDrawer(cartDrawerId);
    return data;
  }

  async function changeLine(key, quantity) {
    setCartLoading(true);
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message);
      await renderCartSections();
    } catch (err) {
      showCartError(err.message);
    } finally {
      setCartLoading(false);
    }
  }

  async function applyDiscount(code) {
    const res = await fetch(`${theme.routes.cartUpdate}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: code }),
    });
    const cart = await res.json();
    await renderCartSections();
    if (!code) return true;
    const match = (cart.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
    return !!(match && match.applicable);
  }

  function initCart() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-line-change]');
      if (btn) {
        e.preventDefault();
        changeLine(btn.dataset.lineKey, Number(btn.dataset.lineChange));
        return;
      }
      const removeCode = e.target.closest('[data-discount-remove]');
      if (removeCode) {
        e.preventDefault();
        setCartLoading(true);
        applyDiscount('').finally(() => setCartLoading(false));
      }
    });

    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-quantity]');
      if (input) changeLine(input.dataset.lineKey, Math.max(0, Number(input.value) || 0));
    });

    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('[data-discount-form]');
      if (!form) return;
      e.preventDefault();
      const input = $('input', form);
      const code = input.value.trim();
      if (!code) return;
      setCartLoading(true);
      try {
        const ok = await applyDiscount(code);
        $$('[data-discount-message]').forEach((el) => {
          el.hidden = false;
          el.textContent = ok ? theme.strings.discountApplied : theme.strings.discountInvalid;
          el.className = ok ? 'small-print text-success' : 'small-print text-danger';
        });
      } finally {
        setCartLoading(false);
      }
    });
  }

  /* Any add-to-cart form (card quick add, PDP, sticky bar). */
  function initProductForms() {
    document.addEventListener('submit', async (e) => {
      const form = e.target.closest('form[data-product-form]');
      if (!form) return;
      e.preventDefault();

      const submitter = e.submitter || $('[type="submit"]', form);
      const buyNow = submitter && submitter.name === 'buy_now';
      const data = new FormData(form);
      const id = Number(data.get('id'));
      const quantity = Number(data.get('quantity') || 1);
      if (!id) return;

      const buttons = $$('[type="submit"]', form);
      const labels = buttons.map((b) => b.innerHTML);
      buttons.forEach((b) => {
        b.disabled = true;
        b.setAttribute('aria-busy', 'true');
      });
      if (submitter && !buyNow) submitter.textContent = theme.strings.adding;
      const errorEl = $('[data-form-error]', form.closest('[data-product]') || form);
      if (errorEl) errorEl.hidden = true;

      try {
        if (buyNow) {
          await fetch(`${theme.routes.cartAdd}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id, quantity }] }),
          }).then(async (r) => {
            if (!r.ok) throw new Error((await r.json()).description);
          });
          window.location.href = '/checkout';
          return;
        }
        await addToCart([{ id, quantity }]);
      } catch (err) {
        if (errorEl) {
          errorEl.textContent = err.message || theme.strings.cartError;
          errorEl.hidden = false;
        }
      } finally {
        buttons.forEach((b, i) => {
          b.innerHTML = labels[i];
          b.removeAttribute('aria-busy');
          b.disabled = b.dataset.soldOut === 'true';
        });
      }
    });
  }

  /* ── Product page: variants, quantity, gallery, sticky bar ─────────── */
  function initProduct(root) {
    const dataEl = $('[data-product-json]', root);
    if (!dataEl) return;
    const product = JSON.parse(dataEl.textContent);
    const forms = $$('form[data-product-form]', root);
    const qtyInputs = $$('[data-qty-input]', root);

    function currentOptions() {
      return $$('[data-option-index]', root)
        .filter((fs) => !fs.closest('[data-sticky-buy]'))
        .map((fs) => {
          const checked = $('input:checked', fs);
          return checked ? checked.value : null;
        });
    }

    function findVariant() {
      const opts = currentOptions();
      return product.variants.find((v) => v.options.every((o, i) => opts[i] == null || o === opts[i]));
    }

    function renderVariant(variant) {
      if (!variant) return;
      forms.forEach((f) => {
        const idInput = $('input[name="id"]', f);
        if (idInput) idInput.value = variant.id;
        $$('[type="submit"]', f).forEach((b) => {
          b.disabled = !variant.available;
          b.dataset.soldOut = String(!variant.available);
          if (b.name !== 'buy_now') b.textContent = variant.available ? theme.strings.addToCart : theme.strings.soldOut;
        });
      });
      $$('[data-price]', root).forEach((el) => {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.classList.toggle('price--on-sale', !!onSale);
        $('.price__current', el).textContent = formatMoney(variant.price);
        const compare = $('.price__compare', el);
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        }
      });
      $$('[data-percent-off]', root).forEach((el) => {
        const pct = variant.compare_at_price > variant.price
          ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
          : 0;
        el.hidden = pct === 0;
        el.textContent = `${pct}% off`;
      });
      $$('[data-sold-out-note]', root).forEach((el) => (el.hidden = variant.available));

      if (variant.featured_media_id) goToMedia(variant.featured_media_id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    root.addEventListener('change', (e) => {
      if (e.target.closest('[data-option-index]')) renderVariant(findVariant());
    });

    // Quantity steppers stay in sync between the panel and the sticky bar.
    root.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (!step) return;
      e.preventDefault();
      const next = Math.max(1, (Number(qtyInputs[0].value) || 1) + Number(step.dataset.qtyStep));
      qtyInputs.forEach((i) => (i.value = next));
    });
    qtyInputs.forEach((input) =>
      input.addEventListener('change', () => {
        const v = Math.max(1, Number(input.value) || 1);
        qtyInputs.forEach((i) => (i.value = v));
      }),
    );

    // Gallery
    const slides = $$('[data-media-id]', root).filter((el) => el.classList.contains('gallery-pdp__slide'));
    const thumbs = $$('[data-thumb-for]', root);
    let manualUntil = 0;

    function goToIndex(index) {
      slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === index)));
      slides.forEach((s, i) => {
        const v = $('video', s);
        if (v && i !== index) v.pause();
      });
    }
    function goToMedia(mediaId) {
      const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index >= 0) {
        manualUntil = Date.now() + 2000;
        goToIndex(index);
      }
    }
    thumbs.forEach((t, i) =>
      t.addEventListener('click', () => {
        manualUntil = Date.now() + 2000;
        goToIndex(i);
      }),
    );
    // Scroll-linked image switching, as on the headless PDP.
    if (slides.length > 1 && window.matchMedia('(min-width: 1024px)').matches) {
      window.addEventListener(
        'scroll',
        () => {
          if (Date.now() < manualUntil) return;
          const el = document.scrollingElement;
          const max = el.scrollHeight - window.innerHeight;
          if (max <= 0) return;
          const next = Math.min(slides.length - 1, Math.floor((el.scrollTop / max) * slides.length));
          if (!slides[next].classList.contains('is-active')) goToIndex(next);
        },
        { passive: true },
      );
    }

    // Sticky buy bar
    const sticky = $('[data-sticky-buy]', root);
    if (sticky) {
      const onScroll = () => sticky.classList.toggle('is-visible', window.scrollY > 280);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* ── Countdowns and scheduled content ──────────────────────────────── */
  const pad = (n) => String(n).padStart(2, '0');
  function parts(ms) {
    const t = Math.max(0, Math.floor(ms / 1000));
    return { d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
  }
  function clockText(ms) {
    const { d, h, m, s } = parts(ms);
    const clock = `${pad(h)}:${pad(m)}:${pad(s)}`;
    return d > 0 ? `${d}d ${clock}` : clock;
  }

  function renderCountdown(el, now) {
    const target = Date.parse(el.dataset.target);
    if (Number.isNaN(target)) return;
    const remaining = target - now;
    if (remaining <= 0) {
      el.hidden = true;
      return;
    }
    el.hidden = false;
    if (el.dataset.variant === 'plain') {
      el.textContent = clockText(remaining);
      return;
    }
    const { d, h, m, s } = parts(remaining);
    el.setAttribute('aria-label', clockText(remaining));
    const unit = (v, label) =>
      `<span class="countdown__unit" aria-hidden="true"><span class="countdown__value">${pad(v)}</span><span class="countdown__label">${label}</span></span>`;
    el.innerHTML =
      (d > 0 ? unit(d, d === 1 ? 'day' : 'days') : '') + unit(h, 'hrs') + unit(m, 'mins') + unit(s, 'secs');
  }

  /*
    [data-schedule data-start data-end] shows the child matching the phase:
    [data-phase="upcoming"], [data-phase="live"], [data-phase="ended"].
    [data-countdown-to] countdowns inside follow the phase's boundary.
  */
  function renderSchedule(el, now) {
    const start = Date.parse(el.dataset.start);
    const end = Date.parse(el.dataset.end);
    let phase = 'live';
    if (!Number.isNaN(start) && now < start) phase = 'upcoming';
    else if (!Number.isNaN(end) && now > end) phase = 'ended';
    if (el.dataset.phaseCurrent === phase) return;
    el.dataset.phaseCurrent = phase;
    $$('[data-phase]', el).forEach((child) => {
      const phases = child.dataset.phase.split(' ');
      child.hidden = !phases.includes(phase);
    });
    // Count down to the start while upcoming, to the end while live.
    $$('[data-countdown-to]', el).forEach((c) => {
      c.dataset.target = phase === 'upcoming' ? el.dataset.start : el.dataset.end;
    });
    el.hidden = phase === 'ended' && el.hasAttribute('data-hide-when-ended');
  }

  function initTimers() {
    const schedules = $$('[data-schedule]');
    const countdowns = $$('[data-countdown]');
    if (!schedules.length && !countdowns.length) return;
    const tick = () => {
      const now = Date.now();
      schedules.forEach((s) => renderSchedule(s, now));
      countdowns.forEach((c) => renderCountdown(c, now));
    };
    tick();
    setInterval(tick, 1000);
  }

  /* ── Scroll reveal ─────────────────────────────────────────────────── */
  function initReveal() {
    const items = $$('.reveal');
    if (!items.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      items.forEach((i) => i.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }),
      { rootMargin: '0px 0px -10% 0px', threshold: 0.08 },
    );
    items.forEach((i) => io.observe(i));
  }

  /* ── Carousels ─────────────────────────────────────────────────────── */
  function initCarousels() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-carousel-step]');
      if (!btn) return;
      const track = $('[data-carousel-track]', btn.closest('[data-carousel]'));
      if (!track) return;
      const card = track.firstElementChild;
      const step = card ? card.getBoundingClientRect().width + 20 : 340;
      track.scrollBy({ left: Number(btn.dataset.carouselStep) * step, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
  }

  /* ── Audible films (hero, Ginger6) ─────────────────────────────────── */
  /*
    Plays while at least half in view, re-mutes when scrolled away. Audible
    autoplay is best-effort: browsers block it without a gesture, so we fall
    back to muted playback and the speaker toggle is the way in. A visitor's
    explicit mute/unmute sticks.
  */
  function initFilms() {
    $$('[data-film]').forEach((wrap) => {
      if (reduceMotion) return;
      const video = $('video', wrap);
      const toggle = $('[data-sound-toggle]', wrap);
      if (!video) return;

      const desktop = video.dataset.srcDesktop;
      const mobile = video.dataset.srcMobile || desktop;
      if (desktop || mobile) {
        const mq = window.matchMedia('(min-width: 1024px)');
        const pick = () => {
          const src = mq.matches ? desktop || mobile : mobile;
          if (src && video.getAttribute('src') !== src) video.src = src;
        };
        pick();
        mq.addEventListener('change', pick);
      }
      video.hidden = false;
      wrap.classList.add('is-loading');
      video.addEventListener('playing', () => wrap.classList.add('is-playing'));

      let choice = null;
      let inView = false;
      const wantsSound = wrap.dataset.film === 'audible';

      const setSound = (on) => {
        video.muted = !on;
        if (toggle) {
          toggle.setAttribute('aria-pressed', String(on));
          toggle.setAttribute('aria-label', on ? 'Mute film' : 'Unmute film');
        }
      };

      const playWithSound = () => {
        const sound = choice === null ? wantsSound : choice;
        setSound(sound);
        video.play().catch(() => {
          setSound(false);
          video.play().catch(() => {});
        });
      };

      new IntersectionObserver(
        ([entry]) => {
          inView = entry.isIntersecting;
          if (inView) playWithSound();
          else {
            setSound(false);
            if (wrap.dataset.pauseOffscreen !== 'false') video.pause();
          }
        },
        { threshold: 0.5 },
      ).observe(video);

      if (toggle) {
        toggle.hidden = false;
        toggle.addEventListener('click', () => {
          choice = video.muted;
          setSound(choice);
          if (inView && video.paused) video.play().catch(() => {});
        });
      }
    });
  }

  /* ── Lazy-play gallery videos ──────────────────────────────────────── */
  function initLazyVideos() {
    const vids = $$('video[data-lazy-src]');
    if (!vids.length) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const v = entry.target;
          v.src = v.dataset.lazySrc;
          v.removeAttribute('data-lazy-src');
          if (!reduceMotion) v.play().catch(() => {});
          io.unobserve(v);
        }),
      { rootMargin: '400px' },
    );
    vids.forEach((v) => io.observe(v));
  }

  /* ── Welcome popup ─────────────────────────────────────────────────── */
  function initPopup() {
    const popup = $('[data-welcome-popup]');
    if (!popup) return;
    const OPTOUT = 'gb:welcome-popup:optout-on';
    const SESSION = 'gb:welcome-popup:seen-session';
    const d = new Date();
    const today = `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
    const read = (store, key) => {
      try {
        return store.getItem(key);
      } catch {
        return null;
      }
    };
    const write = (store, key, value) => {
      try {
        store.setItem(key, value);
      } catch {
        /* storage blocked — the popup is a nice-to-have */
      }
    };
    if (read(localStorage, OPTOUT) === today || read(sessionStorage, SESSION) === '1') return;
    if (window.Shopify && window.Shopify.designMode) return;

    const depth = Number(popup.dataset.scrollDepth || 0.25);
    const delay = Number(popup.dataset.delay || 10) * 1000;
    let fired = false;

    const close = () => {
      popup.classList.remove('is-open');
      popup.setAttribute('aria-hidden', 'true');
    };
    const show = () => {
      if (fired) return;
      if ($('.drawer.is-open')) return; // never stack over the cart
      fired = true;
      clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      write(sessionStorage, SESSION, '1');
      popup.classList.add('is-open');
      popup.setAttribute('aria-hidden', 'false');
      const btn = $('[data-popup-close]', popup);
      if (btn) btn.focus();
    };
    const onScroll = () => {
      const el = document.scrollingElement;
      const max = el.scrollHeight - window.innerHeight;
      if (max > 0 && el.scrollTop / max >= depth) show();
    };
    const timer = setTimeout(show, delay);
    window.addEventListener('scroll', onScroll, { passive: true });

    popup.addEventListener('click', (e) => {
      if (e.target.closest('[data-popup-close]')) close();
      if (e.target.closest('[data-popup-dismiss]')) {
        write(localStorage, OPTOUT, today);
        close();
      }
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && popup.classList.contains('is-open')) close();
    });
    document.addEventListener('drawer:open', close);
  }

  /* ── Collection toolbar ────────────────────────────────────────────── */
  function initCollection() {
    $$('[data-sort-select]').forEach((select) =>
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        url.searchParams.delete('page');
        window.location.href = url.toString();
      }),
    );
  }

  /* ── Bundle builder ────────────────────────────────────────────────── */
  function initBundle() {
    const root = $('[data-bundle]');
    if (!root) return;
    const min = Number(root.dataset.min || 3);
    const percent = Number(root.dataset.percent || 20);
    const picks = new Map(); // variantId -> { qty, price, image, title }

    const tray = $('[data-bundle-tray]', root);
    const status = $('[data-bundle-status]', root);
    const sub = $('[data-bundle-sub]', root);
    const cta = $('[data-bundle-add]', root);
    const slots = $('[data-bundle-slots]', root);
    const error = $('[data-bundle-error]', root);

    function count() {
      let n = 0;
      picks.forEach((p) => (n += p.qty));
      return n;
    }

    function render() {
      const n = count();
      let subtotal = 0;
      picks.forEach((p) => (subtotal += p.qty * p.price));
      const qualifies = n >= min;
      const remaining = Math.max(min - n, 0);

      status.textContent = qualifies
        ? `${n} items — ${percent}% off unlocked`
        : n === 0
          ? `Pick ${min} or more to save ${percent}%`
          : `${n} of ${min} — add ${remaining} more to save ${percent}%`;
      if (n === 0) sub.textContent = 'Your bundle is empty.';
      else if (qualifies) {
        const save = Math.round((subtotal * percent) / 100);
        sub.innerHTML = `<span>${formatMoney(subtotal - save)}</span> <span class="bundle-tray__save">(save ${formatMoney(save)})</span>`;
      } else sub.textContent = formatMoney(subtotal);
      cta.disabled = !qualifies;

      // Slots: one tile per unit, then empty placeholders up to the minimum.
      const tiles = [];
      picks.forEach((p) => {
        for (let i = 0; i < p.qty; i += 1) {
          tiles.push(`<li class="bundle-picked__slot is-filled"><img src="${p.image}" alt="${p.title}" width="64" height="64" loading="lazy"></li>`);
        }
      });
      for (let i = tiles.length; i < min; i += 1) tiles.push(`<li class="bundle-picked__slot" aria-hidden="true">${i + 1}</li>`);
      slots.innerHTML = tiles.join('');

      $$('[data-bundle-item]', root).forEach((item) => {
        const id = item.dataset.variantId;
        const qty = picks.has(id) ? picks.get(id).qty : 0;
        $('[data-bundle-qty]', item).textContent = qty;
        $('[data-bundle-pick]', item).hidden = qty > 0;
        $('[data-bundle-stepper]', item).hidden = qty === 0;
      });
    }

    root.addEventListener('click', async (e) => {
      const item = e.target.closest('[data-bundle-item]');
      const pick = e.target.closest('[data-bundle-pick]');
      const step = e.target.closest('[data-bundle-step]');
      if (item && (pick || step)) {
        const id = item.dataset.variantId;
        const current = picks.get(id) || {
          qty: 0,
          price: Number(item.dataset.price),
          image: item.dataset.image,
          title: item.dataset.title,
        };
        current.qty = Math.max(0, current.qty + (pick ? 1 : Number(step.dataset.bundleStep)));
        if (current.qty === 0) picks.delete(id);
        else picks.set(id, current);
        render();
        return;
      }
      if (e.target.closest('[data-bundle-add]')) {
        const items = [];
        picks.forEach((p, id) => items.push({ id: Number(id), quantity: p.qty }));
        cta.disabled = true;
        error.hidden = true;
        try {
          await addToCart(items);
          picks.clear();
          render();
        } catch (err) {
          error.textContent = err.message;
          error.hidden = false;
          cta.disabled = false;
        }
      }
    });

    if (tray) render();
  }

  /* ── Product recommendations ───────────────────────────────────────── */
  function initRecommendations() {
    $$('[data-recommendations]').forEach(async (el) => {
      if (el.dataset.loaded) return;
      el.dataset.loaded = 'true';
      try {
        const html = await fetch(el.dataset.url).then((r) => r.text());
        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-recommendations]');
        if (fresh && fresh.innerHTML.trim()) el.innerHTML = fresh.innerHTML;
      } catch {
        /* recommendations are optional */
      }
    });
  }

  /* ── Cart note ─────────────────────────────────────────────────────── */
  function initCartNote() {
    let timer;
    document.addEventListener('input', (e) => {
      if (!e.target.matches('textarea[name="note"]')) return;
      clearTimeout(timer);
      const note = e.target.value;
      timer = setTimeout(() => {
        fetch(`${theme.routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        });
      }, 400);
    });
  }

  /* ── Boot ──────────────────────────────────────────────────────────── */
  function boot() {
    initChrome();
    initDrawers();
    initCart();
    initProductForms();
    $$('[data-product]').forEach(initProduct);
    initTimers();
    initReveal();
    initCarousels();
    initFilms();
    initLazyVideos();
    initPopup();
    initCollection();
    initBundle();
    initRecommendations();
    initCartNote();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Re-bind section-scoped features when the theme editor re-renders a section.
  document.addEventListener('shopify:section:load', (e) => {
    $$('[data-product]', e.target).forEach(initProduct);
    initReveal();
    initFilms();
    initRecommendations();
  });

  window.GlowAndBeam = { openDrawer, addToCart, renderCartSections, formatMoney };
})();
